import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="14858" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[14px] font-sans font-normal uppercase tracking-widest text-foreground/80 hover:text-accent transition-all duration-300 hover:shadow-[0_1px_0_0_#FFFF00]">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <nav className="flex flex-col gap-6 mt-8 px-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-sans uppercase tracking-wider text-foreground/90 hover:text-accent transition-colors border-b border-border/10 pb-2" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function StudioCommandHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 border-b border-transparent dark bg-[#161616] text-foreground ${isScrolled ? "bg-[#161616]/80 backdrop-blur-md border-border/20 shadow-xl shadow-black/40" : "bg-[#161616]"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="block hover:opacity-90 transition-opacity">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572105/img/clean_audio_solution_logo.png" alt="LiveAmazing Lettings" className="h-7 w-auto object-contain brightness-0 invert" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="48">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          
          <Button asChild className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-sans text-xs uppercase tracking-widest transition-all duration-300 rounded-sm">
            <Link to="/ContactPage">
              Book Valuation
            </Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-white/5" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full h-[calc(100vh-5rem)] bg-[#161616]/95 backdrop-blur-2xl border-t border-white/10 animate-in slide-in-from-top-5 fade-in duration-200">
          <MenuProvider menu_id="48">
            <MobileMenuConsumer onClose={() => setMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-6 mt-4 border-t border-white/5">
             <Button asChild className="w-full bg-primary text-primary-foreground font-sans uppercase tracking-widest rounded-sm">
              <Link to="/ContactPage" onClick={() => setMobileMenuOpen(false)}>
                Book Valuation
              </Link>
            </Button>
          </div>
        </div>}
    </header>;
}